import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import CommonTabHeader from "Components/Common/CommonTabHeader";
import { Button, Container, Spinner } from "reactstrap";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import "modules/Library/CRF/css/crfpreview.scss";
import "survey-core/survey-core.css";
import { themeJson } from "modules/Library/CRF/helpers/theme";
import { applyRuntimeSurveyLogic } from "modules/Library/CRF/components/Survey/RuntimeValidation/applyRuntimeSurveyLogic";
import { usePermission } from "context/PermissionContext";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import CommonButton from "Components/Common/CommonButton";
import { BreadcrumbContext } from "Layouts/Header";
import { STUDY_PATHS } from "Routes/paths";

interface CRFViewerProps {
  crf: {
    id: number;
    name?: string;
    studyscreeningname?: string;
    json: any;
    crfid: number;
    crfdetails: any;
    selectedCrfDetails?: any;
  };
  onClose: () => void;
  onSave: (data: any) => void;
  isLoading: boolean;
  readonly?: boolean;
  parentBreadcrumbs?: { text: string; link?: string; onclick?: () => void }[];
}
const volunteer = JSON.parse(sessionStorage.getItem("activevolunteer") || "{}");
const CRFViewStudyScreening: React.FC<CRFViewerProps> = ({
  crf,
  onClose,
  onSave,
  isLoading,
  readonly,
  parentBreadcrumbs,
}) => {
  const { hasPermission } = usePermission();
  const canAddCrf = !readonly && (!!parentBreadcrumbs || hasPermission(PERMISSION_MODULES.STUDY_SCREENING, PERMISSION_ACTIONS.SCREENING_CRF_ADD));

  // -----------------------------------------------------
  // Clean JSON
  // -----------------------------------------------------
  const { currentForm, isAlreadyCompleted } = useMemo(() => {
    const details = crf.selectedCrfDetails?.crfdetails || crf.crfdetails;
    const answers = details?.answers;

    return {
      currentForm: details || {},
      isAlreadyCompleted: !!answers && Object.keys(answers).length > 0
    };
  }, [crf]);
  const status = sessionStorage.getItem("active_volunteer_screening_status");

  const [isLocked, setIsLocked] = useState(false);
  useEffect(() => {
    if (!parentBreadcrumbs && (status === `"passed"` || status === `"failed"`)) {
      setIsLocked(true);
    }
  }, [status, parentBreadcrumbs]);

  /* -----------------------------------------------------
   * Breadcrumb context for open CRF
   * ----------------------------------------------------- */
  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;

  const crfTitle = currentForm.title || crf.selectedCrfDetails?.title || crf.name || "CRF Form";

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!parentBreadcrumbs) return;

    if (crfTitle && setBreadcrumbItems) {
      const baseItems = parentBreadcrumbs || [
        { text: "Study Screening", link: STUDY_PATHS.SCREENING_LIST },
        { text: "Screening List", onclick: () => onCloseRef.current?.() },
      ];
      setBreadcrumbItems([
        ...baseItems,
        { text: crfTitle },
      ]);
    }

    return () => {
      if (setBreadcrumbItems) {
        const resetItems = parentBreadcrumbs || [
          { text: "Study Screening", link: STUDY_PATHS.SCREENING_LIST },
          { text: "Screening List" },
        ];
        setBreadcrumbItems(resetItems);
      }
    };
  }, [crfTitle, setBreadcrumbItems, parentBreadcrumbs]);


  // -----------------------------------------------------
  // Create Survey Model
  // -----------------------------------------------------
  const model = useMemo(() => {
    const m = new Model(currentForm);

    m.showTitle = false;
    m.showHeader = false;
    m.applyTheme(themeJson);

    applyRuntimeSurveyLogic(m);

    // Load Answers from currentForm (which has nested crfdetails)
    const answers = currentForm?.answers;

    if (answers && Object.keys(answers).length > 0) {
      m.data = answers;
      m.mode = "display"; // Read only if already saved
    }

    if (!canAddCrf) {
      m.mode = "display"; // Read only if user lacks add permission
    }

    return m;
  }, [currentForm, canAddCrf]);

  const isDisabled = readonly || !!isAlreadyCompleted || isLoading || (parentBreadcrumbs ? false : isLocked);

  return (
    <>
      <Container fluid className="h-100">
        <CommonTabHeader title={currentForm.title || "CRF Form"} />
        <div className="crf-preview">
          <Survey model={model} />
        </div>
      </Container>
      <div className="screening_crf_preview d-flex justify-content-between p-3 bg-white border-top">
        <Button color="secondary" size="sm" outline onClick={onClose}>
          Cancel
        </Button>
        {parentBreadcrumbs ? (
          <Button
            color="primary"
            size="sm"
            onClick={() => {
              const answers = model.data;
              onSave(answers);
            }}
            disabled={isDisabled}
            style={isDisabled ? { cursor: "not-allowed", opacity: 0.5 } : {}}
          >
            {isLoading && <Spinner size="sm" className="me-2" />}
            Save
          </Button>
        ) : (
          <CommonButton
            module={PERMISSION_MODULES.STUDY_SCREENING}
            action={PERMISSION_ACTIONS.SCREENING_CRF_ADD}
            color="primary"
            size="sm"
            onClick={() => {
              const answers = model.data;
              onSave(answers);
            }}
            disabled={isDisabled}
            style={isDisabled ? { cursor: "not-allowed", opacity: 0.5 } : {}}
          >
            {isLoading && <Spinner size="sm" className="me-2" />}
            Save
          </CommonButton>
        )}
      </div>
    </>
  );
};

export default CRFViewStudyScreening;
